import { FunctionComponent, useEffect, useState } from 'react';
import { Typography } from '@mui/material';
import { DatePicker } from './datePicker';
import { FlexContainer } from '../layout/container';
import { DateRange } from '@starjack/utils';
import { getYear, subYears } from 'date-fns';

const reduceYears = (value: Date) => {
    if (!value) return null;
    const year = getYear(value);

    if (year > 2099) {
        return subYears(value, year - 2099);
    }

    return value;
};

const normalizeDateRange = ([from, to]: DateRange): DateRange => [
    reduceYears(from),
    reduceYears(to),
];

type DateRangePickerDesktopProps = {
    onChange: (date: DateRange) => void;
    rangeDelimiter?: string;
    fromText?: string;
    toText?: string;
    title?: string;
    value?: DateRange;
};

export const DateRangePicker: FunctionComponent<
    DateRangePickerDesktopProps
> = ({
    onChange,
    title,
    fromText = 'von',
    toText = 'bis',
    rangeDelimiter = '-',
    value = [null, null],
}) => {
    const [selectedDate, setSelectedDate] = useState<DateRange>(
        normalizeDateRange(value)
    );

    useEffect(() => {
        setSelectedDate(normalizeDateRange(value));
    }, [...value]);

    const handleChange = (type: 'from' | 'to') => (date: Date) => {
        let copyDate: DateRange = [...selectedDate];

        if (type === 'from') {
            copyDate = [date, copyDate[1]];
        } else {
            copyDate = [copyDate[0], date];
        }

        setSelectedDate(copyDate);
        onChange(copyDate);
    };

    return (
        <FlexContainer
            sx={{
                flexDirection: 'column',
            }}
        >
            {title && (
                <Typography
                    sx={{
                        mb: 2,
                    }}
                    variant='h6'
                >
                    {title}
                </Typography>
            )}

            <FlexContainer
                sx={{
                    alignItems: { xs: 'stretch', md: 'center' },
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: { xs: 2, md: 0 },
                }}
            >
                <FlexContainer sx={{ flex: { xs: 'none', md: 1 } }}>
                    <DatePicker
                        label={fromText}
                        onChange={handleChange('from')}
                        value={selectedDate[0]}
                    />
                </FlexContainer>

                <FlexContainer
                    sx={{
                        width: '24px',
                        display: { xs: 'none', md: 'flex' },
                    }}
                    center
                >
                    {rangeDelimiter}
                </FlexContainer>
                <FlexContainer sx={{ flex: { xs: 'none', md: 1 } }}>
                    <DatePicker
                        label={toText}
                        onChange={handleChange('to')}
                        value={selectedDate[1]}
                    />
                </FlexContainer>
            </FlexContainer>
        </FlexContainer>
    );
};
